import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { Clock, Calendar, ArrowRight } from "lucide-react";
interface PackageOption {
  frequency: string;
  hours: string;
  price: string;
}
interface PackageItem {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
  options: PackageOption[];
  buttonText: string;
  buttonLink: string;
}
const packagesData: PackageItem[] = [{
  id: "1-mois",
  title: "Un mois",
  subtitle: "Pour prendre tes marques, entourée.",
  description: "Un relais régulier pendant les premières semaines, pour récupérer et trouver doucement ton rythme avec bébé.",
  imageSrc: "https://slow-mama.com/wp-content/uploads/2026/10/7c5381e6-395d-4cac-9a60-0247f2428842.png?v=88b7uy5iudt",
  imageSrcset: "https://slow-mama.com/wp-content/uploads/2026/06/09eaa64f7e4dff345baf34d4d1599963.jpg?v=eolv8k0cxg9-wvc-srcset",
  imageSizes: "https://slow-mama.com/wp-content/uploads/2026/06/09eaa64f7e4dff345baf34d4d1599963.jpg?v=w8yhnd2gl3l-wvc-sizes",
  imageAlt: "Mère tenant tendrement son nouveau-né lors d'un moment de repos",
  options: [{
    frequency: "1 visite par semaine",
    hours: "4 visites de 3 h · 12 h au total · 65 CHF/h",
    price: "780 CHF"
  }, {
    frequency: "2 visites par semaine",
    hours: "8 visites de 3 h · 24 h au total · 65 CHF/h",
    price: "1'560 CHF"
  }],
  buttonText: "CHOISIR 1 MOIS",
  buttonLink: "/ContactPage?forfait=1-mois"
}, {
  id: "2-mois",
  title: "Deux mois",
  subtitle: "Pour avoir un relais qui revient.",
  description: "Une présence familière sur plusieurs semaines, pendant que le quotidien s'installe et que tes besoins évoluent.",
  imageSrc: "https://slow-mama.com/wp-content/uploads/2026/10/6.png?v=to4e1j8gk5",
  imageSrcset: "https://slow-mama.com/wp-content/uploads/2026/10/e0542f94252070655eb2762dbeb891b9.jpg?v=ax2qgz22dnt-wvc-srcset",
  imageSizes: "https://slow-mama.com/wp-content/uploads/2026/10/e0542f94252070655eb2762dbeb891b9.jpg?v=6g36cyolamu-wvc-sizes",
  imageAlt: "Accompagnement bienveillant et apaisant à domicile pour la maman",
  options: [{
    frequency: "1 visite par semaine",
    hours: "8 visites de 3 h · 24 h au total · 65 CHF/h",
    price: "1'560 CHF"
  }, {
    frequency: "2 visites par semaine",
    hours: "16 visites de 3 h · 48 h au total · 65 CHF/h",
    price: "3'120 CHF"
  }],
  buttonText: "CHOISIR 2 MOIS",
  buttonLink: "/ContactPage?forfait=2-mois"
}, {
  id: "a-la-carte",
  title: "À la carte",
  subtitle: "Pour un coup de pouce ponctuel, sans engagement.",
  description: "Quelques heures pour prendre soin de toi et alléger ta journée. Un repas préparé, un relais avec bébé, un soin ou simplement du temps pour souffler : nous choisissons ensemble ce qui te ferait du bien.",
  imageSrc: "https://slow-mama.com/wp-content/uploads/2026/10/55.png?v=etwz5ugdyy",
  imageSrcset: "https://slow-mama.com/wp-content/uploads/2026/10/25c923fe-473c-4425-997d-a5878033a256.png?v=clsmmnf7gdk-wvc-srcset",
  imageSizes: "https://slow-mama.com/wp-content/uploads/2026/10/25c923fe-473c-4425-997d-a5878033a256.png?v=nij420g411q-wvc-sizes",
  imageAlt: "Illustration délicate de maternité et d'écoute",
  options: [{
    frequency: "À la carte",
    hours: "3 h de soutien · 80 CHF/h",
    price: "240 CHF"
  }],
  buttonText: "CHOISIR À LA CARTE",
  buttonLink: "/ContactPage?forfait=a-la-carte"
}];
export default function PostpartumPackages() {
  return <section id="forfaits-postpartum" data-nav="light" className="relative text-foreground py-16 md:py-24 transition-colors bg-muted">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-12 md:mb-16">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-foreground leading-tight">Soutien post-partum à domicile</h2>

          <p className="font-default text-base sm:text-lg md:text-[18px] text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Choisis combien de temps tu aimerais être entourée. Nous définissons ensemble le rythme de tes visites.
          </p>

          <div className="pt-2">
            <Badge variant="outline" className="py-1.5 px-4 text-xs sm:text-sm font-normal tracking-wide text-foreground border-border bg-secondary/60 rounded-full">
              65 CHF/h · 1 ou 2 visites par semaine · Minimum 3 h par visite
            </Badge>
          </div>
        </div>

        {/* 3 Packages Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch mb-14">
          {packagesData.map((pkg, index) => <div key={pkg.id} data-index={index} className="flex">
              <Card className="flex flex-col justify-between w-full rounded-2xl border border-border bg-card/80 p-0 shadow-xs overflow-hidden transition-all duration-300 hover:border-primary/40 hover:shadow-md">
                {/* Visual Header / Illustration */}
                <div className="relative h-56 sm:h-64 w-full bg-secondary/40 overflow-hidden">
                  <img src={pkg.imageSrc} data-wvc-srcset={pkg.imageSrcset} data-wvc-sizes={pkg.imageSizes} alt={pkg.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center transition-transform duration-500 hover:scale-105" />
                </div>

                {/* Content */}
                <CardContent className="flex flex-col flex-1 p-6 sm:p-8 space-y-6">
                  {/* Title & phrase */}
                  <div className="space-y-2">
                    <h3 className="font-serif text-2xl sm:text-3xl font-medium text-foreground tracking-tight">
                      {pkg.title}
                    </h3>
                    <p className="font-serif italic text-base sm:text-lg text-primary font-normal">
                      {pkg.subtitle}
                    </p>
                  </div>

                  {/* Paragraph */}
                  <p className="font-default text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {pkg.description}
                  </p>

                  {/* Frequency Options Breakdown */}
                  <div className="space-y-3 pt-2">
                    {pkg.options.map((option, optIdx) => <div key={optIdx} className="rounded-lg border border-border/80 bg-background/80 p-3.5 flex flex-col justify-between gap-1 sm:gap-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-default text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            {option.frequency}
                          </span>
                          <span className="font-default text-base sm:text-lg font-bold text-foreground">
                            {option.price}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Clock className="w-3.5 h-3.5 text-primary shrink-0" />
                          <span>{option.hours}</span>
                        </div>
                      </div>)}
                  </div>

                  {/* CTA Button */}
                  <div className="pt-4 mt-auto">
                    <Button asChild className="w-full h-14 bg-primary text-primary-foreground font-semibold text-sm sm:text-base tracking-wider uppercase rounded-xl hover:bg-primary/90 hover:scale-[1.03] hover:shadow-md transition-all duration-300 shadow-xs">
                      <Link to={pkg.buttonLink}>
                        <span>{pkg.buttonText}</span>
                        <ArrowRight className="w-5 h-5 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>

        {/* Bottom Information & Secondary Consultation CTA */}
        <div className="max-w-2xl mx-auto text-center space-y-6 pt-4 border-t border-border/60">
          <p className="font-default text-xs sm:text-sm text-muted-foreground leading-relaxed">
            NB : mon accompagnement est non médical et ne se substitue pas à un suivi avec un.e sage femme, un.e gynécologue, un.e médecin traitant. Si une problématique dépasse mes compétences, je vous orienterai vers un professionnel adapté.
            <div><br /></div>
          </p>

          <div className="space-y-4">
            <div className="pt-1">
              <Button asChild variant="outline" className="h-12 px-6 border-border hover:bg-accent hover:text-accent-foreground text-foreground rounded-xl font-default text-xs sm:text-sm uppercase tracking-wider transition-colors">
                <Link to="/ContactPage">
                  <SafeHydrate><Calendar className="w-4 h-4 mr-1.5 text-primary" /></SafeHydrate>
                  <span>PARLONS DE MON ACCOMPAGNEMENT</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}